import React, { useEffect, useRef } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import AppLayout from "layouts/app-layout";
import AuthLayout from "layouts/auth-layout";
import { APP_PREFIX_PATH, AUTH_PREFIX_PATH } from "configs/AppConfig";
import { isEmpty, isLoaded, useFirestore } from "react-redux-firebase";
import * as dayjs from "dayjs";
import { FIRESTORE_USERS_TABLE } from "../constants/FirestoreConstant";
import { useSelector } from "react-redux";
import Loading from "components/shared-components/Loading";
import { useAuth } from "../data/hooks/useAuth";

// A wrapper that routes user to the dashboard when he/she lands on
// the login page while already authenticated
const CheckAlreadyAuthenticated = ({ children, auth }) => {
  return isEmpty(auth) || !auth.emailVerified ? children : <Navigate to={APP_PREFIX_PATH} replace />;
};

// A wrapper for <Route> that redirects to the login
// screen if you're not yet authenticated or if auth is not
// yet loaded
const PrivateRoute = ({ children, auth }) => {
  return isLoaded(auth) && !isEmpty(auth) ? children : <Navigate to={AUTH_PREFIX_PATH} replace />;
};

// Ingelogd is niet genoeg: het profiel moet een CMS-rol hebben (`role` of
// `cmsRole`, zie functions/permissions.js). Een webshopklant die op de een of
// andere manier een sessie heeft, wordt uitgelogd en terug naar de login
// gestuurd in plaats van een leeg CMS te zien.
const RequireCmsRole = ({ children }) => {
  const { cmsRole, profileLoading, loading, signOut, uid } = useAuth();
  const firestore = useFirestore();
  const ready = !loading && !profileLoading;
  useEffect(() => {
    if (ready && !cmsRole) signOut();
  }, [ready, cmsRole, signOut]);

  // `lastLoginTimestamp` op het profiel: één keer per keer dat het CMS laadt
  // met een ingelogde CMS-gebruiker — dus bij inloggen én bij een refresh van
  // de browser, niet bij elke navigatie. Zelfde veldnaam als de webshop bij
  // klanten gebruikt. De regels staan een self-update toe zolang de
  // beschermde velden (rol, landen, ...) niet mee veranderen.
  const stampedForUid = useRef(null);
  useEffect(() => {
    if (!ready || !cmsRole || !uid || stampedForUid.current === uid) return;
    stampedForUid.current = uid;
    firestore
      .collection(FIRESTORE_USERS_TABLE)
      .doc(uid)
      .update({ lastLoginTimestamp: dayjs().unix() })
      .catch((err) => console.error("Could not update lastLoginTimestamp", err));
  }, [ready, cmsRole, uid, firestore]);
  if (!ready) return <Loading cover="page" />;
  if (!cmsRole) return <Navigate to={AUTH_PREFIX_PATH} replace />;
  return children;
};

export const Views = () => {
  const location = useLocation();
  const auth = useSelector((state) => state.firebaseReducer.auth);

  return (
    <Routes>
      <Route
        path="/*"
        element={
          <>
            <Navigate to={APP_PREFIX_PATH} replace />
          </>
        }
      />
      <Route
        path={AUTH_PREFIX_PATH + "/*"}
        element={
          <>
            <CheckAlreadyAuthenticated auth={auth}>
              <AuthLayout />
            </CheckAlreadyAuthenticated>
          </>
        }
      />
      <Route
        path={APP_PREFIX_PATH + "/*"}
        element={
          <>
            <PrivateRoute auth={auth}>
              <RequireCmsRole>
                <AppLayout location={location} />
              </RequireCmsRole>
            </PrivateRoute>
          </>
        }
      />
    </Routes>
  );
};

export default Views;
